import { Cog6ToothIcon, PencilSquareIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { Loading, Tooltip } from 'react-daisyui';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { tDisabledReasonInfo } from '../../../AutoRespondToggle/AutoRespondToggle.hook';

type tAIResponsePreviewProps = {
  autoRespondEnabled: boolean;
  autoRespondSaving: boolean;
  onAutoRespondToggle: () => void;
  disabledReason: tDisabledReasonInfo | null;
  onIntervene?: () => void;
  onGuide?: () => void;
};

export default function AIResponsePreview(props: tAIResponsePreviewProps) {
  const { t } = useTranslation('chat/chat');
  const isDisabled = !!props.disabledReason;
  const actionsMuted = !props.autoRespondEnabled || isDisabled;

  const isActive = props.autoRespondEnabled && !isDisabled;

  const toggleElement = (
    <input
      type="checkbox"
      className={`toggle toggle-sm ${isDisabled ? 'toggle-disabled opacity-40 cursor-not-allowed' : 'toggle-primary'}`}
      checked={props.autoRespondEnabled}
      onChange={isDisabled ? undefined : props.onAutoRespondToggle}
      disabled={isDisabled}
      aria-label="Auto-responder"
    />
  );

  // Menu rows: leading icon + label, full-bleed, hairline separators.
  // Hover is intentionally NOT transitioned — highlights snap in instantly.
  const menuItemClass =
    'flex items-center gap-2.5 w-full px-4 py-3 text-left text-[13px] text-base-content/80 hover:bg-violet-50 hover:text-violet-700 active:bg-violet-100 disabled:text-base-content/30 disabled:hover:bg-transparent disabled:hover:text-base-content/30 disabled:active:bg-transparent disabled:cursor-default';
  const menuIconClass = 'shrink-0 w-4 h-4 opacity-60';

  return (
    <div className="w-64 shadow-[0_2px_12px_rgba(0,0,0,0.18)] bg-base-100 rounded-xl overflow-hidden">

      {/* Hero header: sparkle avatar + name + colored state, toggle right */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="relative shrink-0">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isActive ? 'bg-violet-100' : 'bg-base-200'}`}>
            <SparklesIcon className={`w-5 h-5 ${isActive ? 'text-violet-500' : 'text-base-content/30'}`} />
          </div>
          {isActive && (
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-success border-2 border-base-100" />
          )}
        </div>
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <p className="text-[13px] font-semibold leading-none">{t('aiResponsePreview.title')}</p>
          <p className={`text-[11px] leading-tight ${isActive ? 'text-success font-medium' : 'text-base-content/50'}`}>
            {isDisabled
              ? props.disabledReason?.message
              : props.autoRespondEnabled
                ? t('aiResponsePreview.activeSubtitle')
                : t('aiResponsePreview.inactiveSubtitle')}
          </p>
          {isDisabled && props.disabledReason?.actionLink && (
            <Link
              to={props.disabledReason.actionLink}
              className="text-[11px] text-primary hover:text-primary/80 transition-colors mt-0.5"
            >
              {props.disabledReason.actionLabel}
            </Link>
          )}
        </div>
        {props.autoRespondSaving ? (
          <Loading size="xs" />
        ) : isDisabled ? (
          <Tooltip message={props.disabledReason?.message ?? ''} position="top">
            {toggleElement}
          </Tooltip>
        ) : (
          toggleElement
        )}
      </div>

      {/* One menu, three rows. Per-chat actions mute when the agent is off;
          config stays live — it's how you get the agent working again. */}
      <div className="divide-y divide-base-200 border-t border-base-200">
        {props.onIntervene && (
          <button onClick={props.onIntervene} disabled={actionsMuted} className={menuItemClass}>
            <PencilSquareIcon className={menuIconClass} />
            {t('smartLeadbot.slbBanner.intervene')}
          </button>
        )}
        {props.onGuide && (
          <button onClick={props.onGuide} disabled={actionsMuted} className={menuItemClass}>
            <ChatBubbleBottomCenterTextIcon className={menuIconClass} />
            {t('smartLeadbot.slbBanner.guide')}
          </button>
        )}
        <Link to="/leadbot/smart-config" className={menuItemClass}>
          <Cog6ToothIcon className={menuIconClass} />
          {t('aiResponsePreview.configLink')}
        </Link>
      </div>

    </div>
  );
}
